import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import StoreHeader from '@/components/store/StoreHeader'
import StoreFooter from '@/components/store/StoreFooter'
import InventoryItemTile from '@/components/store/InventoryItemTile'
import { useInventoryFavorites } from '@/hooks/useInventoryFavorites'
import { api, type InventoryItemNameRow } from '@/lib/api'
import { type SortOption, parseItemsSortParam } from '@/lib/itemsSort'
import { itemAllowsCart, pickClearanceDeals } from '@/lib/inventoryItemDisplay'
import { STOREFRONT_GRID_CLASS, useStorefrontGridColumnCount } from '@/hooks/useStorefrontGridColumnCount'
import { INFINITE_BATCH_ROWS, useInfiniteBatch } from '@/hooks/useInfiniteBatch'

function relevanceScore(row: InventoryItemNameRow, q: string): number {
  const ql = q.toLowerCase()
  const name = (row.item_name || '').toLowerCase()
  const num = (row.item_num || '').toLowerCase()
  const desc = (row.description || '').toLowerCase()
  if (name === ql) return 1000
  if (name.startsWith(ql)) return 900
  if (name.includes(ql)) return 800
  if (num.includes(ql)) return 700
  if (desc.includes(ql)) return 600
  return 500
}

function sortInventoryRows(
  rows: InventoryItemNameRow[],
  sort: SortOption,
  itemQuery: string,
  favoriteIds: Set<string>
): InventoryItemNameRow[] {
  const q = itemQuery.trim().toLowerCase()
  const favRank = (id: string) => (favoriteIds.has(id) ? 0 : 1)

  return [...rows].sort((a, b) => {
    if (sort === 'relevance') {
      if (q) {
        const da = relevanceScore(a, q)
        const db = relevanceScore(b, q)
        if (da !== db) return db - da
      }
      const fa = favRank(a.inventory_id)
      const fb = favRank(b.inventory_id)
      if (fa !== fb) return fa - fb
      return (a.item_name || '').localeCompare(b.item_name || '', undefined, {
        sensitivity: 'base',
      })
    }
    if (sort === 'price_asc') {
      const pa = a.retail_price ?? Number.POSITIVE_INFINITY
      const pb = b.retail_price ?? Number.POSITIVE_INFINITY
      if (pa !== pb) return pa - pb
      return (a.item_name || '').localeCompare(b.item_name || '', undefined, {
        sensitivity: 'base',
      })
    }
    if (sort === 'price_desc') {
      const pa = a.retail_price ?? Number.NEGATIVE_INFINITY
      const pb = b.retail_price ?? Number.NEGATIVE_INFINITY
      if (pa !== pb) return pb - pa
      return (a.item_name || '').localeCompare(b.item_name || '', undefined, {
        sensitivity: 'base',
      })
    }
    if (sort === 'name_asc') {
      return (a.item_name || '').localeCompare(b.item_name || '', undefined, {
        sensitivity: 'base',
      })
    }
    if (sort === 'name_desc') {
      return (b.item_name || '').localeCompare(a.item_name || '', undefined, {
        sensitivity: 'base',
      })
    }
    if (sort === 'availability') {
      const sa = a.in_stock ?? -1
      const sb = b.in_stock ?? -1
      if (sa !== sb) return sb - sa
      return (a.item_name || '').localeCompare(b.item_name || '', undefined, {
        sensitivity: 'base',
      })
    }
    return 0
  })
}

function filterItems(
  items: InventoryItemNameRow[],
  categoryId: string | null,
  itemQuery: string
): InventoryItemNameRow[] {
  let rows = items
  if (categoryId) {
    rows = rows.filter((r) => r.category_id === categoryId)
  }
  const q = itemQuery.trim().toLowerCase()
  if (q) {
    rows = rows.filter(
      (row) =>
        row.item_name.toLowerCase().includes(q) ||
        row.item_num.toLowerCase().includes(q) ||
        (row.description && row.description.toLowerCase().includes(q))
    )
  }
  return rows
}

export default function ItemsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryId = searchParams.get('category')
  const itemQuery = searchParams.get('item') ?? ''
  const favoritesOnly = searchParams.get('favorites') === '1'
  /** `?deals=1`: the same "Clearance & deals" set the home rail previews. */
  const dealsOnly = searchParams.get('deals') === '1'

  const [items, setItems] = useState<InventoryItemNameRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { isFavorite, toggle, favoriteIds } = useInventoryFavorites()

  const gridCols = useStorefrontGridColumnCount('items')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.storefront
      .getInventoryItemNames()
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) setItems(res.data.items)
        else setItems([])
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message)
          setItems([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    let rows = filterItems(dealsOnly ? pickClearanceDeals(items) : items, categoryId, itemQuery)
    if (favoritesOnly) {
      rows = rows.filter((r) => favoriteIds.has(r.inventory_id))
    } else {
      // Browsing (All, a category, deals, search) hides out-of-stock items; saved favorites keep them.
      rows = rows.filter((r) => itemAllowsCart(r.in_stock))
    }
    return rows
  }, [items, dealsOnly, categoryId, itemQuery, favoritesOnly, favoriteIds])

  const sort = parseItemsSortParam(searchParams.get('sort'))

  const displayList = useMemo(
    () => sortInventoryRows(filtered, sort, itemQuery, favoriteIds),
    [filtered, favoriteIds, sort, itemQuery]
  )

  // Infinite scroll: five rows at a time; any filter or sort change starts again from the top.
  const { visible, hasMore, sentinelRef } = useInfiniteBatch(
    displayList.length,
    INFINITE_BATCH_ROWS * gridCols,
    `${categoryId ?? ''}\t${itemQuery}\t${favoritesOnly ? 1 : 0}\t${dealsOnly ? 1 : 0}\t${sort}`
  )
  const shownItems = displayList.slice(0, visible)

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-orange-50 via-stone-100 to-stone-300/60">
      <StoreHeader inventoryItems={items} inventoryLoading={loading} />

      <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 pb-6 pt-3 sm:px-6 sm:pt-4 lg:px-8 lg:pb-8">
        {loading ? (
          <div className={STOREFRONT_GRID_CLASS}>
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="card animate-pulse overflow-hidden border-stone-200/90 bg-white"
              >
                <div className="aspect-[4/3] bg-stone-200/70" />
                <div className="space-y-2 p-4">
                  <div className="h-4 w-3/4 rounded bg-stone-200" />
                  <div className="h-3 w-full rounded bg-stone-100" />
                  <div className="mt-4 h-6 w-1/3 rounded bg-stone-200/80" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="text-red-600">{error}</p>
        ) : displayList.length === 0 ? (
          <div className="card border-stone-200/90 bg-white p-8 text-center text-stone-600">
            <p>
              {favoritesOnly
                ? 'No favorite items yet. Tap the heart on items to add them here.'
                : dealsOnly
                  ? 'No clearance or deal items match right now. Check back soon.'
                  : 'No items match your filters.'}
            </p>
            <Link to="/" className="inline-block mt-4 text-primary-600 hover:underline">
              Back to home
            </Link>
          </div>
        ) : (
          <>
            <div className={STOREFRONT_GRID_CLASS}>
              {shownItems.map((item) => (
                <InventoryItemTile
                  key={item.inventory_id}
                  item={item}
                  isFavorite={isFavorite(item.inventory_id)}
                  onToggleFavorite={() => toggle(item.inventory_id)}
                />
              ))}
            </div>
            {hasMore ? (
              <div ref={sentinelRef} className="flex justify-center py-6 text-stone-400" aria-hidden>
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <p className="py-6 text-center text-xs text-stone-500 sm:text-sm">
                {displayList.length} {displayList.length === 1 ? 'item' : 'items'}
              </p>
            )}
          </>
        )}

        <StoreFooter detailsClassName="mt-10 sm:mt-12" />
      </main>
    </div>
  )
}
